import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { BTN_PRIMARY, BTN_GHOST, ICON_BTN, INPUT } from "@/lib/styles";
import { X, Camera, ImageUp, ScanText, Loader2, Coffee, ReceiptText, FileText, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

async function fileToCompressedBase64(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width >= height && width > maxDim) { height = Math.round((height * maxDim) / width); width = maxDim; }
      else if (height > width && height > maxDim) { width = Math.round((width * maxDim) / height); height = maxDim; }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("img")); };
    img.src = url;
  });
}

const PURPOSES = [
  { id: "structured", label: "Nota", Icon: FileText },
  { id: "literal", label: "Literal", Icon: ScanText },
  { id: "receipt", label: "Recibo → Gasto", Icon: ReceiptText },
];

export function ScanModal({ open, folderId = "", onClose }) {
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [purpose, setPurpose] = useState("structured");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // expense confirm form
  const [meta, setMeta] = useState(null);

  if (!open) return null;

  const reset = () => { setPreview(""); setDraft(null); };
  const close = () => { if (!busy) { reset(); onClose?.(); } };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Selecciona una imagen (JPG/PNG)."); return; }
    try { setPreview(await fileToCompressedBase64(file)); }
    catch { toast.error("No se pudo leer la imagen."); }
  };

  const extract = async () => {
    if (!preview) { toast.info("Primero toma o sube una foto."); return; }
    setBusy(true);
    try {
      if (purpose === "receipt") {
        const [{ data: r }, m] = await Promise.all([
          api.post("/ai/scan-receipt", { image_base64: preview }),
          meta ? Promise.resolve({ data: meta }) : api.get("/finance/meta"),
        ]);
        if (!meta) setMeta(m.data);
        const scopes = m.data?.scopes || meta?.scopes || ["Personal"];
        if (!r.is_receipt) toast.info("No parece un recibo, pero puedes ajustar los datos manualmente.");
        setDraft({
          amount: r.total ? String(r.total) : "",
          currency: r.currency || "VES",
          scope: scopes[0],
          category: r.category || "Otros",
          date: r.date || format(new Date(), "yyyy-MM-dd"),
          note: [r.merchant, r.summary].filter(Boolean).join(" · ").slice(0, 160),
        });
        toast.success(`Recibo leído · quedan ${r.remaining_ml} ml ☕`);
      } else {
        const { data } = await api.post("/ai/ocr", { image_base64: preview, mode: purpose });
        const { data: note } = await api.post("/notes", { type: "text", title: data.title || "Texto escaneado", content: data.outline || "", folder_id: folderId || null });
        toast.success(`Nota creada · quedan ${data.remaining_ml} ml ☕`);
        reset(); onClose?.(); navigate(`/note/${note.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveExpense = async () => {
    const amt = parseFloat(String(draft.amount).replace(",", "."));
    if (isNaN(amt) || amt <= 0) { toast.error("Ingresa un monto válido"); return; }
    setBusy(true);
    try {
      await api.post("/finance/transactions", {
        kind: "expense", amount: amt, currency: draft.currency,
        scope: draft.scope, category: draft.category, date: draft.date, note: draft.note,
      });
      toast.success("Gasto registrado");
      reset(); onClose?.(); navigate("/finanzas");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const scopes = meta?.scopes || [];
  const cats = meta?.expense_categories || [];
  const currencies = meta?.currencies || ["VES", "USD", "EUR", "USDT", "USDC"];

  return (
    <div className="fixed inset-0 z-[75] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="scan-modal">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2">
            {draft ? <><ReceiptText size={20} className="text-emerald-500" /> Registrar gasto</> : <><ScanText size={20} className="text-indigo-500" /> Escanear</>}
          </h3>
          <button onClick={close} className={ICON_BTN} data-testid="scan-close"><X size={16} /></button>
        </div>

        {draft ? (
          /* ---- Confirm expense ---- */
          <>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">Revisa los datos detectados y confírmalos.</p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} placeholder="Monto" inputMode="decimal" className={`${INPUT} flex-1`} data-testid="receipt-amount" autoFocus />
                <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className={`${INPUT} w-28`} data-testid="receipt-currency">
                  {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <select value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })} className={`${INPUT} flex-1`} data-testid="receipt-scope">
                  {scopes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className={`${INPUT} flex-1`} data-testid="receipt-category">
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className={INPUT} data-testid="receipt-date" />
              <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Nota (comercio)" className={INPUT} data-testid="receipt-note" />
            </div>
            <div className="flex justify-between gap-2 mt-5">
              <button onClick={() => setDraft(null)} className={BTN_GHOST} disabled={busy} data-testid="receipt-back"><ArrowLeft size={15} /> Atrás</button>
              <button onClick={saveExpense} className={BTN_PRIMARY} disabled={busy} data-testid="receipt-save">{busy ? <><Loader2 size={15} className="animate-spin" /> Guardando...</> : "Registrar gasto"}</button>
            </div>
          </>
        ) : (
          /* ---- Capture ---- */
          <>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-1.5">
              Extrae el texto de una imagen. <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><Coffee size={13} /> 10 ml</span>
            </p>

            {preview ? (
              <div className="relative mb-4">
                <img src={preview} alt="preview" className="w-full max-h-52 object-contain rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950" data-testid="scan-preview" />
                <button onClick={() => setPreview("")} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="scan-clear"><X size={14} /></button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button onClick={() => cameraRef.current?.click()} className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors" data-testid="scan-camera-btn">
                  <Camera size={26} className="text-indigo-500" /><span className="font-body text-xs">Tomar foto</span>
                </button>
                <button onClick={() => fileRef.current?.click()} className="flex flex-col items-center justify-center gap-2 py-6 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors" data-testid="scan-upload-btn">
                  <ImageUp size={26} className="text-indigo-500" /><span className="font-body text-xs">Subir imagen</span>
                </button>
              </div>
            )}

            <div className="mb-4">
              <span className="font-mono2 text-[11px] text-slate-400 block mb-1.5">¿Qué quieres hacer?</span>
              <div className="grid grid-cols-3 gap-1 bg-slate-100 dark:bg-slate-800/60 rounded-xl p-1">
                {PURPOSES.map((p) => (
                  <button key={p.id} onClick={() => setPurpose(p.id)} className={`py-2 rounded-lg text-[11px] font-medium transition-colors flex flex-col items-center gap-1 ${purpose === p.id ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid={`scan-mode-${p.id}`}>
                    <p.Icon size={15} /> {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={close} className={BTN_GHOST} disabled={busy} data-testid="scan-cancel">Cancelar</button>
              <button onClick={extract} className={BTN_PRIMARY} disabled={busy || !preview} data-testid="scan-run">
                {busy ? <><Loader2 size={15} className="animate-spin" /> Procesando...</> : <><ScanText size={15} /> {purpose === "receipt" ? "Leer recibo" : "Extraer texto"}</>}
              </button>
            </div>
          </>
        )}

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="scan-camera-input" />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="scan-file-input" />
      </div>
    </div>
  );
}
